import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { usePaddleCheckout } from '@/hooks/usePaddleCheckout';
import { supabase } from '@/integrations/supabase/client';
import { getPaymentEnvironment } from '@/lib/paddle';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ChevronLeft, Crown, Check, Sprout, ExternalLink, Shield, Zap, Infinity,
} from 'lucide-react';
import { PaymentTestModeBanner } from '@/components/PaymentTestModeBanner';

const FREE_FEATURES = [
  { label: 'Up to 5 plants', icon: Sprout },
  { label: 'Basic schedule generation', icon: Zap },
];

const PRO_FEATURES = [
  { label: 'Unlimited plants', icon: Infinity },
  { label: 'Full schedule generation', icon: Zap },
  { label: 'All plant types', icon: Sprout },
  { label: 'Priority support', icon: Shield },
];

export default function Subscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscription, isPro, loading: subLoading, freePlantLimit } = useSubscription();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleSubscribe = async (priceId: string) => {
    if (!user) return;
    await openCheckout({
      priceId,
      customerEmail: user.email,
      customData: { userId: user.id },
    });
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const env = getPaymentEnvironment();
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { returnUrl: window.location.href, environment: env === 'sandbox' ? 'sandbox' : 'live' },
      });
      if (error || !data?.url) throw new Error('Could not open portal');
      window.open(data.url, '_blank');
    } catch {
      toast.error('Could not open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (subLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Sprout className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <div className="p-4 md:p-8">
        <div className="mx-auto max-w-2xl">
          {/* Header */}
          <div className="mb-8 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
                Subscription
              </h1>
              <p className="text-sm text-muted-foreground">Manage your plan and billing</p>
            </div>
          </div>

          {/* Current Plan Status */}
          <Card className={`mb-6 border-2 ${isPro ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {isPro ? (
                    <Crown className="h-5 w-5 text-primary" />
                  ) : (
                    <Sprout className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="text-lg font-bold text-foreground">
                    {isPro ? 'Pro Plan' : 'Free Plan'}
                  </span>
                </div>
                <Badge
                  variant={isPro ? 'default' : 'secondary'}
                  className={isPro ? 'bg-primary text-primary-foreground' : ''}
                >
                  {isPro ? 'Active' : 'Current'}
                </Badge>
              </div>

              {isPro && subscription && (
                <div className="space-y-2 text-sm text-muted-foreground">
                  {subscription.current_period_end && (
                    <p>
                      {subscription.cancel_at_period_end ? 'Access until' : 'Renews on'}:{' '}
                      <span className="font-medium text-foreground">
                        {format(new Date(subscription.current_period_end), 'dd MMM yyyy')}
                      </span>
                    </p>
                  )}
                  {subscription.cancel_at_period_end && (
                    <p className="text-destructive text-xs font-medium">
                      Your subscription will not renew after this period
                    </p>
                  )}
                </div>
              )}

              {!isPro && (
                <p className="text-sm text-muted-foreground">
                  You can grow up to <strong className="text-foreground">{freePlantLimit} plants</strong> on the free plan.
                </p>
              )}

              {isPro && (
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  variant="outline"
                  className="mt-4 w-full"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {portalLoading ? 'Opening...' : 'Manage Billing & Invoices'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Plans */}
          {!isPro && (
            <>
              <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Upgrade to Pro
              </h2>

              <div className="grid gap-4 sm:grid-cols-2 mb-6">
                {/* Monthly */}
                <Card className="border-2 border-primary/30 relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-primary text-primary-foreground text-[10px]">Popular</Badge>
                  </div>
                  <CardContent className="pt-6 pb-5">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-foreground">£3.99</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">Cancel anytime</p>
                    <Button
                      onClick={() => handleSubscribe('pro_monthly')}
                      disabled={checkoutLoading}
                      className="w-full garden-gradient"
                    >
                      {checkoutLoading ? 'Loading...' : 'Subscribe Monthly'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Annual */}
                <Card className="border-2 border-border relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <Badge variant="secondary" className="text-[10px]">Save 31%</Badge>
                  </div>
                  <CardContent className="pt-6 pb-5">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-foreground">£33</span>
                      <span className="text-sm text-muted-foreground">/year</span>
                    </div>
                    <p className="text-xs text-primary font-medium mb-4">£2.75/month equivalent</p>
                    <Button
                      onClick={() => handleSubscribe('pro_annual')}
                      disabled={checkoutLoading}
                      variant="outline"
                      className="w-full"
                    >
                      {checkoutLoading ? 'Loading...' : 'Subscribe Annually'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Feature Comparison */}
          <h2 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isPro ? 'Your Pro Features' : 'What you get with Pro'}
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            {/* Free column */}
            <Card className={`border-2 ${!isPro ? 'border-primary/20' : 'border-border'}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sprout className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground text-sm">Free</span>
                </div>
                <ul className="space-y-2">
                  {FREE_FEATURES.map(f => (
                    <li key={f.label} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {f.label}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Pro column */}
            <Card className={`border-2 ${isPro ? 'border-primary/30 bg-primary/5' : 'border-primary/20'}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-foreground text-sm">Pro</span>
                </div>
                <ul className="space-y-2">
                  {PRO_FEATURES.map(f => (
                    <li key={f.label} className="flex items-center gap-2 text-sm text-foreground">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      {f.label}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
